import {
    AssessmentFilterStatus,
    AssessmentFilterStatusText,
    IssueFilter
} from "../../lib/dashboard/types/IssueFilter";
import {
    Box,
    Chip
} from "@mui/material";
import {
    ReactNode,
    useMemo
} from "react";

import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IssueStatus } from "../../models";
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import TagIcon from '@mui/icons-material/Tag';
import { format } from "date-fns";
import { issueStatusText } from "../../componentsV2/issue/issuesStatusText";
import { useIssueFilter } from "./contexts/IssueFilterContext";
import { useTags } from "./contexts/TagsContextProvider";

type AppliedFilterChipsProps = {
    locationIdToLocationNameMap: Map<string, string>;
    issueIdToIssueNameMap: Map<string, string>;
};

type AppliedFilterChipInfo = {
    type: keyof IssueFilter;
    label: ReactNode;
    value: string;
    onDelete: () => void;
};

const filterDisplayOrder: Array<keyof IssueFilter> = [
    "issueId",
    "assessmentId",
    "date",
    "statuses",
    "locationIds",
    "tagIds"
];

export const AppliedFilterChips = (props: AppliedFilterChipsProps) => {
    const { issueIdToIssueNameMap, locationIdToLocationNameMap } = props;
    const { tagIdToTagMap } = useTags();
    const { filter, updateFilter } = useIssueFilter();

    const appliedChipInfos: Array<AppliedFilterChipInfo> = useMemo(() => {
        return Object.entries(filter)
            .map(([key, value]) => {
                switch (key as keyof IssueFilter) {
                    case "date":
                        const dateFilter: IssueFilter["date"] = value as IssueFilter["date"] | undefined;
                        if (!dateFilter?.start && !dateFilter?.end) {
                            return null;
                        }
                        const startDateStr = dateFilter?.start ? format(dateFilter.start, "yyyy/MM/dd") : "N/A";
                        const endDateStr = dateFilter?.end ? format(dateFilter.end, "yyyy/MM/dd") : "N/A";
                        if (startDateStr === endDateStr) {
                            return {
                                type: "date",
                                label: <CalendarMonthOutlinedIcon fontSize="small" />,
                                value: startDateStr,
                                onDelete: () => updateFilter({ ...filter, date: undefined })
                            };
                        }
                        return {
                            type: "date",
                            label: <CalendarMonthOutlinedIcon fontSize="small" />,
                            value: `${startDateStr} - ${endDateStr}`,
                            onDelete: () => updateFilter({ ...filter, date: undefined })
                        };
                    case "locationIds":
                        const locationIds = value as Array<string> | undefined;
                        return locationIds?.map(locationId => ({
                            type: "locationIds",
                            label: <PlaceOutlinedIcon fontSize="small" />,
                            value: locationIdToLocationNameMap.get(locationId) ?? locationId,
                            onDelete: () => {
                                const newLocationIds = filter.locationIds?.filter(id => id !== locationId) ?? undefined;
                                updateFilter({ ...filter, locationIds: newLocationIds?.length ? newLocationIds : undefined });
                            }
                        }));
                    case "issueId":
                        const issueId = value as string | undefined;
                        return issueId ? {
                            type: "issueId",
                            label: "Issue: ",
                            value: issueIdToIssueNameMap.get(issueId) ?? issueId,
                            onDelete: () => updateFilter({ ...filter, issueId: undefined })
                        } : null;
                    case "statuses":
                        const statuses = value as IssueFilter["statuses"] | undefined;
                        return statuses?.map(status => ({
                            type: "statuses",
                            label: <InfoOutlinedIcon fontSize="small" />,
                            value: issueStatusText[status as IssueStatus] || AssessmentFilterStatusText[status as AssessmentFilterStatus],
                            onDelete: () => {
                                const newStatuses = filter.statuses?.filter(s => s !== status) ?? undefined;
                                updateFilter({ ...filter, statuses: newStatuses?.length ? newStatuses : undefined });
                            }
                        }));
                    case "assessmentId":
                        const assessmentId = value as string | undefined;
                        return assessmentId ? {
                            type: "assessmentId",
                            label: "Assessment: ",
                            value: assessmentId,
                            onDelete: () => updateFilter({ ...filter, assessmentId: undefined })
                        } : null;
                    case "tagIds":
                        const tagIds = value as Array<string> | undefined;
                        return tagIds?.map(tagId => ({
                            type: "tagIds",
                            label: <TagIcon fontSize="small" />,
                            value: tagIdToTagMap.get(tagId)?.name ?? tagId,
                            onDelete: () => {
                                const newTagIds = filter.tagIds?.filter(id => id !== tagId) ?? undefined;
                                updateFilter({ ...filter, tagIds: newTagIds?.length ? newTagIds : undefined });
                            }
                        }));
                    default:
                        return null;
                }
            })
            .filter(Boolean)
            .flat()
            .sort((a, b) => filterDisplayOrder.indexOf((a as AppliedFilterChipInfo).type) - filterDisplayOrder.indexOf((b as AppliedFilterChipInfo).type)) as Array<AppliedFilterChipInfo>;
    }, [filter, issueIdToIssueNameMap, locationIdToLocationNameMap, tagIdToTagMap, updateFilter]);

    return (
        <>
            {appliedChipInfos?.map((chipInfo, index) => (
                <Chip
                    key={index}
                    label={(
                        <Box display="flex" alignItems="center" gap={0.5}>
                            {chipInfo.label}
                            {chipInfo.value}
                        </Box>
                    )}
                    variant="outlined"
                    onDelete={chipInfo.onDelete}
                />
            ))}
        </>
    );
};